import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { CommonModule, DOCUMENT } from '@angular/common';
import { Component, effect, inject, input, numberAttribute, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import {
  lucideBookOpen,
  lucideCircleAlert,
  lucideClock,
  lucideFileText,
  lucideNewspaper,
  lucideRefreshCw,
  lucideSearch,
  lucideX,
} from '@ng-icons/lucide';
import { BrnSelectImports } from '@spartan-ng/brain/select';
import { HlmButtonDirective } from '@spartan-ng/ui-button-helm';
import { HlmIconDirective } from '@spartan-ng/ui-icon-helm';
import { HlmInputDirective } from '@spartan-ng/ui-input-helm';
import { HlmLabelDirective } from '@spartan-ng/ui-label-helm';
import { HlmNumberedPaginationComponent } from '@spartan-ng/ui-pagination-helm';
import { HlmSelectImports } from '@spartan-ng/ui-select-helm';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ErrorMessageComponent } from '../../components/error-message/error-message.component';
import { LoadingSpinnerComponent } from '../../components/loading-spinner/loading-spinner.component';
import { NewsCardComponent } from '../../components/news-card/news-card.component';
import { NewsArticle, PaginatedNewsResponse } from '../../models/news-article.model';
import { NewsService } from '../../services/news.service';
import { SEOService } from '../../services/seo.service';
import { StructuredDataService } from '../../services/structured-data.service'; // Keep this for the service itself
import { DataFeedElementSchema, DataFeedSchema, NewsArticleSchema } from '../../types/structured-data.interfaces'; // Updated import path for schema types

@Component({
  selector: 'app-news',
  standalone: true,
  imports: [
    CommonModule,
    NewsCardComponent,
    LoadingSpinnerComponent,
    ErrorMessageComponent,
    HlmButtonDirective,
    HlmInputDirective,
    HlmNumberedPaginationComponent,
    ReactiveFormsModule,
    FormsModule,
    NgIconComponent,
    HlmIconDirective,
    HlmLabelDirective,
    BrnSelectImports,
    HlmSelectImports,
  ],
  templateUrl: './news.component.html',
  styleUrl: './news.component.scss',
  providers: [
    provideIcons({
      lucideRefreshCw,
      lucideCircleAlert,
      lucideFileText,
      lucideSearch,
      lucideX,
      lucideNewspaper,
      lucideBookOpen,
      lucideClock,
    }),
  ],
})
export class NewsComponent {
  private newsService = inject(NewsService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private seoService = inject(SEOService);
  private structuredDataService = inject(StructuredDataService);
  private breakpointObserver = inject(BreakpointObserver);
  private document = inject(DOCUMENT);

  page = input(1, { transform: (value: unknown) => numberAttribute(value, 1) });
  pageSize = input(12, { transform: (value: unknown) => numberAttribute(value, 12) });
  search = input<string>(''); // Input from URL
  type = input<'all' | 'news' | 'blog'>('all'); // Input for article type filter - changed from articleType to type
  timeFilter = input<'anytime' | 'day' | 'week'>('anytime'); // Input for time filter

  // Initialize FormControls with values from the URL inputs
  searchInputControl = new FormControl(this.search(), { nonNullable: true });
  sortControl = new FormControl<'anytime' | 'day' | 'week'>('anytime', { nonNullable: true });

  articles = signal<NewsArticle[]>([]);
  loading = signal(true);
  error = signal<string | null>(null);
  totalItems = signal(0);
  typeFilter = signal<'all' | 'news' | 'blog'>('all'); // Signal to track active type filter

  responsiveMaxSize = signal(7); // Default maxSize

  sortOptions = [
    { value: 'anytime', label: 'Anytime' },
    { value: 'day', label: 'Past 24 hours' },
    { value: 'week', label: 'Past Week' },
  ];

  protected readonly PAGE_SIZES = [12, 24, 36, 48];

  constructor() {
    const currentParams = this.activatedRoute.snapshot.queryParams;
    const paramsToSet: Record<string, string | number> = {};
    let performNavigation = false;

    // Normalize 'search' parameter
    const expectedSearch = this.search(); // Default '' if QP is missing
    // Add search to QPs if it's completely missing (undefined)
    if (typeof currentParams['search'] === 'undefined') {
      paramsToSet['search'] = expectedSearch;
      performNavigation = true;
    }

    // Normalize 'page' parameter
    const expectedPage = this.page(); // Default 1 if QP is missing
    if (!currentParams['page']) {
      paramsToSet['page'] = expectedPage;
      performNavigation = true;
    }

    // Normalize 'pageSize' parameter
    const expectedPageSize = this.pageSize(); // Default 12 if QP is missing
    if (!currentParams['pageSize']) {
      paramsToSet['pageSize'] = expectedPageSize;
      performNavigation = true;
    }

    // Normalize 'type' parameter
    const expectedType = this.type(); // Default 'all' if QP is missing
    if (!currentParams['type']) {
      paramsToSet['type'] = expectedType;
      performNavigation = true;
    }

    // Normalize 'timeFilter' parameter
    const expectedTimeFilter = this.timeFilter(); // Default 'anytime' if QP is missing
    if (!currentParams['timeFilter']) {
      paramsToSet['timeFilter'] = expectedTimeFilter;
      performNavigation = true;
    }

    if (performNavigation) {
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: paramsToSet,
        queryParamsHandling: 'merge',
        replaceUrl: true, // Avoid adding to browser history for this normalization
      });
      // The component inputs will update from the new URL, and the effect will run.
    }

    this.breakpointObserver
      .observe([Breakpoints.XSmall, Breakpoints.Small])
      .pipe(takeUntilDestroyed())
      .subscribe(result => {
        if (result.breakpoints[Breakpoints.XSmall]) {
          this.responsiveMaxSize.set(3);
        } else if (result.breakpoints[Breakpoints.Small]) {
          this.responsiveMaxSize.set(5);
        } else {
          this.responsiveMaxSize.set(7); // Default for larger screens
        }
      });

    // Effect to react to changes in page, pageSize, search, type, and time filter inputs (from URL)
    effect(() => {
      const currentPage = this.page();
      const currentPageSize = this.pageSize();
      let currentSearchTermFromInput = this.search(); // Value from URL
      const currentArticleType = this.type(); // Value from URL
      const currentTimeFilter = this.timeFilter(); // Value from URL

      if (typeof currentSearchTermFromInput === 'undefined') {
        currentSearchTermFromInput = '';
      }

      // Sync searchInputControl with URL's search param
      if (this.searchInputControl.value !== currentSearchTermFromInput) {
        this.searchInputControl.setValue(currentSearchTermFromInput, { emitEvent: false });
      }

      // Sync sortControl with URL's timeFilter param
      if (this.sortControl.value !== currentTimeFilter) {
        this.sortControl.setValue(currentTimeFilter, { emitEvent: false });
      }

      // Sync typeFilter with URL's articleType param
      this.typeFilter.set(currentArticleType);

      this.loadArticles(
        currentPage,
        currentPageSize,
        currentSearchTermFromInput,
        currentArticleType,
        currentTimeFilter
      );
    });

    // Subscribe to searchInputControl value changes for debouncing
    this.searchInputControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed())
      .subscribe(debouncedSearchValue => {
        // Only perform search if more than 1 character or empty string (to clear search)
        if (debouncedSearchValue.length > 1 || debouncedSearchValue.length === 0) {
          // Check against the current URL state (via this.search() signal)
          if (debouncedSearchValue !== this.search()) {
            this.router.navigate([], {
              relativeTo: this.activatedRoute,
              queryParams: { search: debouncedSearchValue, page: 1 }, // Reset to page 1
              queryParamsHandling: 'merge',
            });
          }
        }
      });

    // Subscribe to sortControl valueChanges
    this.sortControl.valueChanges.pipe(distinctUntilChanged(), takeUntilDestroyed()).subscribe(newTimeFilterValue => {
      // Check against the current URL state (via this.timeFilter() signal)
      if (newTimeFilterValue !== this.timeFilter()) {
        this.router.navigate([], {
          relativeTo: this.activatedRoute,
          queryParams: { timeFilter: newTimeFilterValue, page: 1 }, // Reset to page 1
          queryParamsHandling: 'merge',
        });
      }
    });

    this.setSeoMetadata();
  }

  loadArticles(
    page: number,
    pageSize: number,
    search?: string,
    articleType: 'all' | 'news' | 'blog' = 'all',
    timeFilter: 'anytime' | 'day' | 'week' = 'anytime'
  ) {
    this.loading.set(true);
    this.error.set(null);
    const effectivePage = Math.max(1, page);

    this.newsService.getNews(effectivePage, pageSize, search, articleType, timeFilter).subscribe({
      next: (response: PaginatedNewsResponse) => {
        this.articles.set(response.items);
        this.totalItems.set(response.total);
        this.loading.set(false);
        this.addNewsPageStructuredData(response.items); // Call new method for structured data
      },
      error: (_err: unknown) => {
        this.error.set('Failed to load news articles. Please try again later.');
        this.loading.set(false);
        console.error('Error loading news:', _err);
      },
    });
  }

  onPageChange(newPage: number) {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { page: newPage },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  onPageSizeChange(newPageSize: number | string) {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { page: 1, pageSize: Number(newPageSize) }, // Reset to page 1
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  onTypeFilterChange(newType: 'all' | 'news' | 'blog') {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { type: newType, page: 1 }, // Reset to page 1 - changed from articleType to type
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  clearSearch() {
    this.searchInputControl.setValue('');
  }

  // New method to add structured data for the news page
  private addNewsPageStructuredData(articles: NewsArticle[]) {
    this.structuredDataService.addBaseNodes();

    const breadcrumbItems = [
      { name: 'Home', item: `${environment.canonicalOrigin}/` },
      { name: 'News' }, // Current page, no item URL
    ];
    this.structuredDataService.addBreadcrumb(breadcrumbItems);

    const dataFeedElements: DataFeedElementSchema[] = articles.map(article => {
      const canonicalArticleUrl = `${environment.canonicalOrigin}/news/${article.slug}`;
      const articleSchema: NewsArticleSchema = {
        '@type': 'NewsArticle',
        '@id': `${canonicalArticleUrl}/#article`,
        headline: article.title,
        url: canonicalArticleUrl,
        image: `${environment.canonicalOrigin}/assets/og/black-og-image-1x.png`,
        author: {
          '@type': 'Organization',
          '@id': `${environment.canonicalOrigin}/#organization`,
        },
      };
      return {
        '@type': 'DataFeedItem',
        item: articleSchema,
        dateCreated: article.createdAt,
      };
    });

    const mainEntity: DataFeedSchema = {
      '@type': 'DataFeed',
      dataFeedElement: dataFeedElements,
    };

    this.structuredDataService.addCollectionPage({
      name: 'AI News and Blog',
      description:
        'Get the latest AI news and summaries, curated by AI for relevance and clarity. Stay updated on artificial intelligence developments and breakthroughs.',
      mainEntity: mainEntity,
      searchPath: '/news', // Added searchPath
    });

    this.structuredDataService.generateAndInjectSchema();
  }

  private setSeoMetadata() {
    const canonicalUrl = `${environment.canonicalOrigin}/news/`;
    this.seoService.updateTags({
      title: 'AI News and Blog | Summerry',
      description:
        'Get the latest AI news and summaries, curated by AI for relevance and clarity. Stay updated on artificial intelligence developments and breakthroughs.',
      keywords: 'AI news, artificial intelligence news, tech news, news summaries, machine learning, AI developments',
      type: 'website',
      url: canonicalUrl,
    });
  }
}
